import type { SpanMarker } from '../logic/spanNote';
import type { Span } from '../engine/types';
import type { CommentRoute } from '../logic/offdiff';
import type { Hunk } from '../logic/suggestEdit';

/** Where a new comment goes. */
export interface Anchor {
  path: string;
  side: 'LEFT' | 'RIGHT';
  line: number;
  startLine: number;
  /** How it is saved (see `routeComment`): a line comment, a file-level thread, or a held draft. */
  route: CommentRoute;
  /** Commit whose text the selected side shows (off-diff comments name it); resolved on save when null. */
  commit: string | null;
  resolveCommit?: () => Promise<string | null>;
  /** Source text of the selected lines (for quoting and suggestions). */
  text: string[];
  prefill?: string;
  /** A span of the lines' text the comment is on (logic/spanNote): shown apart in the composer, and quoted with its marker when saved. */
  span?: SpanMarker;
  /** Open on a suggested edit of the lines, where they take one. */
  suggest?: boolean;
  /** The lines take line comments, but not within one diff hunk (so the comment goes as a file comment). */
  spansHunks?: boolean;
  /**
   * For a new-side line comment, the file a suggested change edits: its
   * lines at the head, the head's highlight (when known), and the runs of
   * lines that take line comments (the diff's hunks).
   */
  file?: { lines: readonly string[]; spans: Span[][] | null; hunks: Hunk[] };
}

export interface LineSelection {
  path: string;
  side: 'LEFT' | 'RIGHT';
  start: number;
  end: number;
  dragging: boolean;
}

export function anchorKey(side: 'LEFT' | 'RIGHT', line: number): string {
  return (side === 'LEFT' ? 'L:' : 'R:') + line;
}

export interface MoveLink {
  label: string;
  title: string;
  go: () => void;
}

/** A line the keyboard line cursor can rest on (one per displayed row). */
export interface LineStop {
  /** Side and line the cursor shows: deletions on the old side, everything else on the new. */
  side: 'LEFT' | 'RIGHT';
  line: number;
  /** The row's line on each side, where it has one (context rows have both). */
  oldLine: number | null;
  newLine: number | null;
  block: string;
}

/** The line cursor's position (a side and a line of the current file). */
export interface LineCursor {
  side: 'LEFT' | 'RIGHT';
  line: number;
}

type StopRow = { kind: 'context' | 'add' | 'del'; old: number | null; new: number | null };

/** Every row of the unfolded blocks, in display order, but those `hidden` returns for their block (folded into a stub). */
export function lineStops(
  blocks: { key: string; rows: StopRow[] }[],
  folded: (key: string) => boolean,
  hidden: (key: string) => ReadonlySet<number> | undefined = () => undefined,
): LineStop[] {
  const out: LineStop[] = [];
  for (const b of blocks) {
    if (folded(b.key)) continue;
    const skip = hidden(b.key);
    for (const [i, r] of b.rows.entries()) {
      if (skip?.has(i)) continue;
      const oldLine = r.kind !== 'add' ? r.old : null;
      const newLine = r.kind !== 'del' ? r.new : null;
      if (newLine !== null) out.push({ side: 'RIGHT', line: newLine, oldLine, newLine, block: b.key });
      else if (oldLine !== null) out.push({ side: 'LEFT', line: oldLine, oldLine, newLine, block: b.key });
    }
  }
  return out;
}

/** Index of the stop showing `cursor`, or -1. */
export function stopIndex(stops: LineStop[], cursor: LineCursor | null): number {
  if (!cursor) return -1;
  return stops.findIndex((s) => (cursor.side === 'LEFT' ? s.oldLine : s.newLine) === cursor.line && s.side === cursor.side);
}

/**
 * The line on `side` a range anchored there reaches when the cursor is at
 * stop `to`: the stop's own line on that side, else the nearest stop back
 * toward the anchor (stop `from`) that has one. A range stays on one side,
 * because GitHub comments cover lines of one side.
 */
export function rangeEnd(stops: LineStop[], side: 'LEFT' | 'RIGHT', from: number, to: number): number | null {
  const step = to >= from ? -1 : 1;
  for (let i = to; step < 0 ? i >= from : i <= from; i += step) {
    const n = side === 'LEFT' ? stops[i]?.oldLine : stops[i]?.newLine;
    if (n !== null && n !== undefined) return n;
  }
  return null;
}
